
import { useState, useEffect } from "react";
import { db } from "./Firebase";
import {
  addDoc,
  collection,
  onSnapshot,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import Navbar from "./Navbar";
import { DeleteTwoTone } from "@mui/icons-material";

export default function Products() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // Search query state
  const [showModal, setShowModal] = useState(false); // To control modal visibility
  const [modalProduct, setModalProduct] = useState(null); // Store product for modal

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "Products"), (snapshot) => {
      const products = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setData(products);
    });

    return () => unsubscribe();
  }, []);

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setImagePreview(url);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const name = formData.get("name");
    const price = formData.get("price");
    const description = formData.get("description");
    const imageUrl = formData.get("imageUrl").trim();

    try {
      const productData = {
        Name: name,
        Price: Number(price),
        Description: description,
        ImageUrl: imageUrl || "",
        CreatedAt: serverTimestamp(),
      };

      if (editMode) {
        await updateDoc(doc(db, "Products", editProduct.id), productData);
        alert("Product updated successfully!");
        setEditMode(false);
        setEditProduct(null);
      } else {
        await addDoc(collection(db, "Products"), productData);
        alert("Product added successfully!");
      }

      e.target.reset();
      setImagePreview("");
    } catch (error) {
      console.error("Error submitting product:", error);
      alert("Failed to submit product. See console for details.");
    } finally {
      setLoading(false);
    }
  };

  const editDetails = (product) => {
    setEditProduct(product);
    setEditMode(true);
    setImagePreview(product.ImageUrl || "");
  };

  const delt = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteDoc(doc(db, "Products", id));
        alert("Product deleted successfully.");
      } catch (error) {
        console.error("Error deleting product:", error);
        alert("Failed to delete product.");
      }
    }
  };

  const showProductDetails = (product) => {
    setModalProduct(product); // Set the product to display in the modal
    setShowModal(true); // Show the modal
  };

  const closeModal = () => {
    setShowModal(false); // Close the modal
    setModalProduct(null); // Clear the modal product
  };

  const handleDeleteInModal = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteDoc(doc(db, "Products", id));
        alert("Product deleted successfully.");
        closeModal();
      } catch (error) {
        console.error("Error deleting product:", error);
        alert("Failed to delete product.");
      }
    }
  };

  const handleEditInModal = (product) => {
    setEditProduct(product);
    setEditMode(true);
    setImagePreview(product.ImageUrl || "");
    closeModal(); // Close the modal when editing
  };

  // Filtered data based on search query
  const filteredData = data.filter((product) =>
    product.Name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Navbar />
      <div className="row m-0 p-4 bg-light text-dark">
        <h1 className="mb-4">PRODUCTS</h1>

        <div className="col-lg-4">
          <div className="card bg-light">
            <div className="card-body">
              <form onSubmit={submit}>
                <input
                  type="text"
                  name="name"
                  placeholder="Product Name"
                  className="form-control my-2"
                  defaultValue={editMode ? editProduct.Name : ""}
                  required
                />
                <input
                  type="number"
                  name="price"
                  placeholder="Price"
                  className="form-control my-2"
                  defaultValue={editMode ? editProduct.Price : ""}
                  required
                />
                <textarea
                  name="description"
                  placeholder="Description"
                  className="form-control my-2"
                  defaultValue={editMode ? editProduct.Description : ""}
                  required
                />
                <input
                  type="url"
                  name="imageUrl"
                  placeholder="Image URL (https://...)"
                  className="form-control my-2"
                  defaultValue={editMode ? editProduct.ImageUrl : ""}
                  onChange={handleImageUrlChange}
                />
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="img-fluid my-2 rounded shadow"
                    style={{ maxHeight: "150px" }}
                  />
                )}

                <button className="btn btn-primary w-100 fw-bold" disabled={loading}>
                  {loading
                    ? editMode
                      ? "Updating..."
                      : "Adding..."
                    : editMode
                    ? "Update Product"
                    : "Add Product"}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-8">
          <div className="card bg-light text-dark">
            <div className="card-body">
              {/* Search bar */}
              <div className="mb-4">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search Products by Name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="row">
                {/* Display each product in a smaller card */}
                {filteredData.map((product) => (
                  <div className="col-md-3 mb-4" key={product.id}>
                    <div className="card h-100" onClick={() => showProductDetails(product)}>
                      <img
                        src={product.ImageUrl || "https://via.placeholder.com/150"}
                        alt={product.Name}
                        className="card-img-top"
                        style={{ height: "150px", objectFit: "cover" }}
                      />
                      <div className="card-body text-center">
                        <h5 className="card-title">{product.Name}</h5>
                      </div>
                    </div>
                  </div>
                ))}

                {/* If no products are found */}
                {filteredData.length === 0 && (
                  <div className="col-12">
                    <div className="alert alert-info text-center">
                      No products found.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for showing product details */}
      {showModal && modalProduct && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{ display: "block" }}
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{modalProduct.Name}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <img
                  src={modalProduct.ImageUrl || "https://via.placeholder.com/150"}
                  alt={modalProduct.Name}
                  className="img-fluid mb-3"
                  style={{ maxHeight: "300px", objectFit: "cover" }}
                />
                <p><strong>Description:</strong> {modalProduct.Description}</p>
                <p><strong>Price:</strong> ₹{modalProduct.Price}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={() => handleEditInModal(modalProduct)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => handleDeleteInModal(modalProduct.id)}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
